import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import KitchenLoader from './KitchenLoader.jsx'

// Square POS connection — replaces CSV import for any merchant on Square.
//
// Two states:
//   1. Not connected — token input + sandbox/production toggle + Connect
//      button. If a sibling unit in the same group is already connected,
//      a "Copy connection from <sibling>" shortcut appears so the second
//      unit doesn't have to re-auth.
//   2. Connected — merchant id, location names, last sync time, "Sync now"
//      + Disconnect. When the account exposes more than one location the
//      operator is asked to assign ONE location to THIS unit (a Square
//      account can back several units; each unit syncs only its location).
//
// Token storage: `restaurants.square_access_token` (RLS-protected).
// All actual API calls happen server-side via the square-oauth /
// square-sync edge fns, which load the token, verify tenant membership,
// and proxy to Square's REST endpoints.
export default function SquareConnectSection({ restaurant, onChanged }) {
  const restaurantId = restaurant?.id
  const isConnected  = !!restaurant?.square_access_token
  const groupId      = restaurant?.group_id || null

  const [token, setToken]               = useState('')
  const [environment, setEnvironment]   = useState('sandbox')
  const [busy, setBusy]                 = useState(false)
  const [error, setError]               = useState(null)
  const [info, setInfo]                 = useState(null)

  // Location picker state.
  const [locations, setLocations]       = useState([])   // [{id, name}]
  const [pickerOpen, setPickerOpen]     = useState(false)
  const [locBusy, setLocBusy]           = useState(false)

  // Sibling-copy state.
  const [siblings, setSiblings]         = useState([])    // connected siblings in the group

  // Whether THIS (connected) unit shares its Square merchant with another
  // unit in the same group. Drives the "pick a location to finish setup"
  // gate when connected-but-unmapped — see HIGH-2.
  const [sharesMerchant, setSharesMerchant] = useState(false)

  // Mirror of the location-mapping gate so handleSync (defined above the
  // render-time derived flag) reads the current value at call time without a
  // stale closure.
  const needsLocationMappingRef = useRef(false)

  // Mask the existing token in the input for display purposes.
  useEffect(() => {
    if (isConnected) {
      setEnvironment(restaurant.square_environment || 'sandbox')
      setToken('') // never echo the actual token; user re-enters if rotating
    }
  }, [isConnected, restaurant?.square_environment])

  // Fetch the merchant's locations from the server. Returns the array so
  // callers can branch on length (auto-assign vs. show picker).
  const fetchLocations = useCallback(async () => {
    const { data, error: fnErr } = await supabase.functions.invoke('square-oauth', {
      body: { restaurant_id: restaurantId, kind: 'list_locations' },
    })
    if (fnErr) throw fnErr
    if (!data?.ok) throw new Error(data?.message || 'Could not list Square locations.')
    return Array.isArray(data.locations) ? data.locations : []
  }, [restaurantId])

  // Map this unit to one location. Selection ONLY happens when there is
  // more than one location; a single-location account is auto-assigned.
  const applyLocation = useCallback(async (loc) => {
    const { data, error: fnErr } = await supabase.functions.invoke('square-oauth', {
      body: {
        restaurant_id: restaurantId,
        kind: 'set_location',
        square_location_id: loc.id,
        square_location_name: loc.name,
      },
    })
    if (fnErr) throw fnErr
    if (!data?.ok) {
      // location_taken: keep the picker open and surface the friendly msg.
      const err = new Error(data?.message || 'Could not assign that location.')
      err.code = data?.error
      throw err
    }
    return data
  }, [restaurantId])

  // After a connection exists but no location is mapped yet, resolve the
  // location: 1 -> auto-assign silently; >1 -> open the picker.
  const resolveLocation = useCallback(async () => {
    setLocBusy(true)
    setError(null)
    try {
      const locs = await fetchLocations()
      setLocations(locs)
      if (locs.length > 1) {
        setPickerOpen(true)
      } else if (locs.length === 1) {
        await applyLocation(locs[0])
        setPickerOpen(false)
        onChanged?.()
      }
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SquareConnectSection] resolveLocation failed', e)
      setError(`Could not load Square locations: ${e?.message || 'try again.'}`)
    } finally {
      setLocBusy(false)
    }
  }, [fetchLocations, applyLocation, onChanged])

  // On mount / when connection changes: if connected but no location is
  // mapped yet, run location resolution so the picker can appear.
  useEffect(() => {
    if (isConnected && !restaurant?.square_location_id) {
      resolveLocation()
    } else {
      setPickerOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, restaurant?.square_location_id, restaurantId])

  // Find connected sibling units in the same group (RLS-scoped client
  // query). Only meaningful when this unit is grouped and NOT connected.
  useEffect(() => {
    let cancelled = false
    async function loadSiblings() {
      if (!groupId || isConnected || !restaurantId) {
        setSiblings([])
        return
      }
      const { data, error: qErr } = await supabase
        .from('restaurants')
        .select('id, name, square_merchant_id')
        .eq('group_id', groupId)
        .not('square_merchant_id', 'is', null)
        .neq('id', restaurantId)
      if (qErr) {
        // eslint-disable-next-line no-console
        console.error('[SquareConnectSection] sibling lookup failed', qErr)
        return
      }
      if (!cancelled) setSiblings((data || []).filter((s) => s.square_merchant_id))
    }
    loadSiblings()
    return () => { cancelled = true }
  }, [groupId, isConnected, restaurantId])

  // Detect whether this CONNECTED unit shares its Square merchant with a
  // sibling in the same group. When it does AND no location is mapped, the
  // server refuses orders sync (location_unmapped) to avoid commingling, so
  // the UI gates "Sync now" behind the location picker too. A legacy
  // ungrouped unit (no group_id) or a sole merchant occupant never trips
  // this, matching the server guard.
  useEffect(() => {
    let cancelled = false
    async function checkSharedMerchant() {
      const merchantId = restaurant?.square_merchant_id || null
      if (!groupId || !isConnected || !merchantId || !restaurantId) {
        setSharesMerchant(false)
        return
      }
      const { data, error: qErr } = await supabase
        .from('restaurants')
        .select('id')
        .eq('group_id', groupId)
        .eq('square_merchant_id', merchantId)
        .neq('id', restaurantId)
        .limit(1)
      if (qErr) {
        // eslint-disable-next-line no-console
        console.error('[SquareConnectSection] shared-merchant check failed', qErr)
        return
      }
      if (!cancelled) setSharesMerchant((data || []).length > 0)
    }
    checkSharedMerchant()
    return () => { cancelled = true }
  }, [groupId, isConnected, restaurantId, restaurant?.square_merchant_id])

  async function handleSelectLocation(loc) {
    if (locBusy) return
    setLocBusy(true)
    setError(null)
    setInfo(null)
    try {
      await applyLocation(loc)
      setPickerOpen(false)
      setInfo(`This unit now syncs only the "${loc.name}" location.`)
      onChanged?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SquareConnectSection] set_location failed', e)
      // location_taken: keep the picker open so they can pick another.
      setError(e?.message || 'Could not assign that location.')
    } finally {
      setLocBusy(false)
    }
  }

  async function handleCopyFromSibling(sibling) {
    if (busy) return
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('square-oauth', {
        body: {
          restaurant_id: restaurantId,
          kind: 'copy_from_sibling',
          sibling_restaurant_id: sibling.id,
        },
      })
      if (fnErr) throw fnErr
      if (!data?.ok) throw new Error(data?.message || 'Could not copy the Square connection.')
      setInfo('Square connection copied. Now choose this unit’s location.')
      // Refresh the parent so isConnected flips; the mount effect will run
      // resolveLocation. Also resolve right here for immediate feedback.
      onChanged?.()
      await resolveLocation()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SquareConnectSection] copy_from_sibling failed', e)
      setError(`Could not copy connection: ${e?.message || 'try again.'}`)
    } finally {
      setBusy(false)
    }
  }

  // OAuth flow — kicks the user out to Square's hosted consent screen.
  async function handleConnectOAuth() {
    if (busy) return
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('square-oauth', {
        body: { restaurant_id: restaurantId, kind: 'authorize_url', environment },
      })
      if (fnErr) throw fnErr
      if (!data?.ok || !data.authorize_url) {
        throw new Error(data?.message || 'Could not generate Square authorize URL.')
      }
      window.location.href = data.authorize_url
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SquareConnectSection] OAuth start failed', e)
      setError(`Could not start Square connection: ${e?.message || 'try again.'}`)
    } finally {
      setBusy(false)
    }
  }

  // Manual paste-token flow — fallback for users with a Personal Access
  // Token (e.g. legacy sandbox testing). New users use OAuth above.
  async function handleConnect() {
    if (busy) return
    if (!token.trim()) {
      setError('Paste your Square Access Token to connect.')
      return
    }
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      const { error: upErr } = await supabase
        .from('restaurants')
        .update({
          square_access_token: token.trim(),
          square_environment:  environment,
          square_sync_status:  null,
        })
        .eq('id', restaurantId)
      if (upErr) throw upErr

      const { data, error: fnErr } = await supabase.functions.invoke('square-sync', {
        body: { restaurant_id: restaurantId, kind: 'test' },
      })
      if (fnErr) throw fnErr
      if (!data?.ok) {
        await supabase
          .from('restaurants')
          .update({ square_access_token: null, square_sync_status: null })
          .eq('id', restaurantId)
        throw new Error(data?.message || 'Square rejected the token.')
      }

      const locs = data.location_names?.length ? ` (${data.location_names.join(', ')})` : ''
      setInfo(`Connected — merchant ${data.merchant_id || 'unknown'}, ${data.locations || 0} location${data.locations === 1 ? '' : 's'}${locs}.`)
      setToken('')
      onChanged?.()
      // Run location resolution so a multi-location account prompts for a
      // pick (and a single-location account auto-assigns).
      await resolveLocation()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SquareConnectSection] connect failed', e)
      setError(`Could not connect: ${e?.message || 'try again.'}`)
    } finally {
      setBusy(false)
    }
  }

  async function handleSync(kind = 'all') {
    if (busy) return
    // HIGH-2 client guard: don't fire an orders sync the server will refuse
    // for a shared-merchant grouped unit with no location mapped. Steer the
    // operator into the location picker instead.
    if (needsLocationMappingRef.current) {
      setError('Pick a location to finish setup before syncing.')
      resolveLocation()
      return
    }
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('square-sync', {
        body: { restaurant_id: restaurantId, kind, days: 30 },
      })
      if (fnErr) throw fnErr
      if (!data?.ok) throw new Error(data?.message || 'Sync failed.')

      const parts = []
      if (typeof data.items_synced === 'number')        parts.push(`${data.items_synced} item${data.items_synced === 1 ? '' : 's'}`)
      if (typeof data.line_items_inserted === 'number') parts.push(`${data.line_items_inserted} sale day${data.line_items_inserted === 1 ? '' : 's'}`)
      if (typeof data.orders_synced === 'number')       parts.push(`${data.orders_synced} order${data.orders_synced === 1 ? '' : 's'}`)
      setInfo(parts.length ? `Synced ${parts.join(', ')} from Square.` : 'Sync complete.')
      onChanged?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SquareConnectSection] sync failed', e)
      setError(`Sync failed: ${e?.message || 'try again.'}`)
    } finally {
      setBusy(false)
    }
  }

  async function handleDisconnect() {
    if (busy) return
    if (!window.confirm('Disconnect Square? Your synced items and sales stay; we just stop pulling new data until you reconnect.')) return
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      const { error: upErr } = await supabase
        .from('restaurants')
        .update({
          square_access_token: null,
          square_merchant_id:  null,
          square_location_id:  null,
          square_location_name: null,
          square_sync_status:  null,
        })
        .eq('id', restaurantId)
      if (upErr) throw upErr
      setInfo('Disconnected. Reconnect any time.')
      setToken('')
      setLocations([])
      setPickerOpen(false)
      onChanged?.()
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[SquareConnectSection] disconnect failed', e)
      setError(`Disconnect failed: ${e?.message || 'try again.'}`)
    } finally {
      setBusy(false)
    }
  }

  // Format last-sync time human-readably.
  const lastSync = restaurant?.square_last_synced_at
    ? new Date(restaurant.square_last_synced_at).toLocaleString()
    : null

  // HIGH-2 gate: a connected grouped unit that shares its merchant with a
  // sibling but has no location mapped must finish the location picker before
  // it can sync — otherwise the server refuses (location_unmapped). Block
  // "Sync now" and prompt the operator to pick a location.
  const needsLocationMapping =
    isConnected && !restaurant?.square_location_id && sharesMerchant
  needsLocationMappingRef.current = needsLocationMapping

  return (
    <section className="mt-6 rounded-2xl border border-black/10 bg-white p-4 shadow-sm">
      <div className="flex items-baseline justify-between gap-2">
        <div>
          <div className="text-xs font-medium text-black/55">
            Square POS
          </div>
          <p className="mt-1 text-xs text-black/60 leading-snug">
            Auto-sync your menu items and sales from Square. Replaces the
            manual CSV import — runs daily, or hit Sync now whenever.
          </p>
        </div>
        {isConnected && (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-medium text-emerald-900">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
            Connected
          </span>
        )}
      </div>

      {error && (
        <div className="mt-3 rounded-md bg-red-50 px-3 py-2 text-sm text-red-800">
          {error}
        </div>
      )}
      {info && (
        <div className="mt-3 rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-900">
          {info}
        </div>
      )}

      {/* Location picker — only shown when the account has >1 location and
          this unit hasn't been mapped yet. */}
      {pickerOpen && locations.length > 1 && (
        <div className="mt-3 rounded-lg border border-blue-200 bg-blue-50/50 p-3">
          <div className="text-xs font-medium text-black/70">
            Choose this unit's Square location
          </div>
          <p className="mt-1 text-[11px] text-black/55 leading-snug">
            This Square account has more than one location. Pick the one
            this unit operates from — we'll sync only that location's sales.
          </p>
          {locBusy ? (
            <div className="mt-3">
              <KitchenLoader size="sm" align="row" messages={['Saving your location…']} />
            </div>
          ) : (
            <div className="mt-3 space-y-2">
              {locations.map((loc) => (
                <button
                  key={loc.id}
                  type="button"
                  onClick={() => handleSelectLocation(loc)}
                  className="flex w-full items-center gap-2 rounded-lg border border-black/15 bg-white px-3 py-2 text-left text-sm font-medium text-black/75 hover:border-blue-400 hover:bg-blue-50"
                >
                  <span className="h-2 w-2 shrink-0 rounded-full border border-blue-400" />
                  {loc.name || loc.id}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {!isConnected ? (
        <div className="mt-3 space-y-3">
          {/* Sibling-copy shortcut: a connected unit already exists in this
              group, so reuse its Square credentials instead of re-auth. */}
          {siblings.length > 0 && (
            <div className="rounded-lg border border-blue-200 bg-blue-50/50 p-3">
              <div className="text-xs font-medium text-black/70">
                Already connected on another unit
              </div>
              <p className="mt-1 text-[11px] text-black/55 leading-snug">
                Reuse the same Square account — you'll just pick this unit's
                location, no re-login needed.
              </p>
              <div className="mt-2 space-y-2">
                {siblings.map((s) => (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => handleCopyFromSibling(s)}
                    disabled={busy}
                    className="w-full rounded-lg border border-blue-300 bg-white px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    Copy Square connection from {s.name}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <label className="mb-1 block text-xs font-medium text-black/70">
              Environment
            </label>
            <div className="flex gap-2">
              {[
                { id: 'sandbox',    label: 'Sandbox (testing)' },
                { id: 'production', label: 'Production (real data)' },
              ].map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setEnvironment(opt.id)}
                  className={`flex-1 rounded-lg border px-3 py-2 text-xs font-medium transition ${
                    environment === opt.id
                      ? 'border-blue-600 bg-blue-50 text-blue-900'
                      : 'border-black/15 bg-white text-black/65 hover:bg-black/[0.02]'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* PRIMARY: OAuth — one-tap, no token-pasting */}
          <button
            type="button"
            onClick={handleConnectOAuth}
            disabled={busy}
            className="w-full rounded-lg bg-[#006AFF] px-4 py-3 text-sm font-medium text-white shadow-sm hover:bg-[#0058D6] active:bg-[#004FB8] disabled:cursor-not-allowed disabled:opacity-50"
          >
            {busy ? 'Opening Square…' : '✨ Connect with Square'}
          </button>
          <p className="text-[11px] text-black/55 leading-snug">
            Opens Square's site so you can grant us read-only access to your menu
            and sales. We never see your password and we can't charge your account.
          </p>

          {/* FALLBACK: paste token. */}
          <details className="rounded-lg border border-black/10 bg-black/[0.02] p-3 text-xs text-black/65">
            <summary className="cursor-pointer font-medium text-black/70">
              Or: paste a Personal Access Token
            </summary>
            <div className="mt-3 space-y-2">
              <input
                id="sq-token"
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="EAAAEh…"
                className="w-full rounded-lg border border-black/15 px-3 py-2 font-mono text-xs outline-none focus:border-blue-600 focus:ring-2 focus:ring-blue-600/30"
              />
              <div className="text-[11px] text-black/55 leading-snug">
                Use this for sandbox testing. Real merchants should use the
                Connect with Square button above.
              </div>
              <button
                type="button"
                onClick={handleConnect}
                disabled={busy || !token.trim()}
                className="w-full rounded-lg border border-blue-300 bg-white px-3 py-2 text-xs font-medium text-blue-700 hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {busy ? 'Connecting…' : 'Connect with token'}
              </button>
            </div>
          </details>
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          <div className="rounded-lg border border-black/10 bg-black/[0.02] p-3 text-xs text-black/70 space-y-1">
            <div>
              <span className="font-medium text-black/55">Environment:</span>{' '}
              <span className="font-medium">{restaurant.square_environment || 'sandbox'}</span>
            </div>
            {restaurant.square_merchant_id && (
              <div>
                <span className="font-medium text-black/55">Merchant ID:</span>{' '}
                <span className="font-mono">{restaurant.square_merchant_id}</span>
              </div>
            )}
            {restaurant.square_location_name && (
              <div>
                <span className="font-medium text-black/55">Location:</span>{' '}
                <span className="font-medium">{restaurant.square_location_name}</span>
              </div>
            )}
            {lastSync && (
              <div>
                <span className="font-medium text-black/55">Last sync:</span>{' '}
                {lastSync}
                {restaurant.square_sync_status && (
                  <span className={`ml-2 rounded px-1.5 py-0.5 text-[10px] font-medium ${
                    restaurant.square_sync_status === 'ok'      ? 'bg-emerald-100 text-emerald-900' :
                    restaurant.square_sync_status === 'syncing' ? 'bg-blue-100 text-blue-900' :
                    'bg-red-100 text-red-900'
                  }`}>
                    {restaurant.square_sync_status}
                  </span>
                )}
              </div>
            )}
          </div>

          {needsLocationMapping && (
            <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              <div className="font-medium">Pick a location to finish setup</div>
              <p className="mt-1 leading-snug">
                This unit shares a Square account with another unit. Choose
                which location it operates from before syncing, so each unit
                only pulls its own sales.
              </p>
              <button
                type="button"
                onClick={resolveLocation}
                disabled={locBusy}
                className="mt-2 rounded-lg border border-amber-400 bg-white px-3 py-1.5 text-xs font-medium text-amber-800 hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {locBusy ? 'Loading locations…' : 'Choose location'}
              </button>
            </div>
          )}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => handleSync('all')}
              disabled={busy || needsLocationMapping}
              title={needsLocationMapping ? 'Pick a location to finish setup before syncing.' : undefined}
              className="flex-1 rounded-lg bg-blue-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Syncing…' : 'Sync now'}
            </button>
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={busy}
              className="rounded-lg border border-red-300 px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50"
            >
              Disconnect
            </button>
          </div>
        </div>
      )}
    </section>
  )
}
